import { Transforms, Element, Editor, createEditor } from 'slate';
import type { SlateBlock } from '@atlas/renderer';
import createTextBlock from '@atlas/tool/box/slate/createTextBlock';
import normalizeTable from '@atlas/tool/box/slate/normalizeTable';
import getNode from '@atlas/tool/box/slate/getNode';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import isListBlock from '@atlas/tool/box/slate/isListBlock';
import isTableBlock from '@atlas/tool/box/slate/isTableBlock';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import normalizeCalloutBlock from '@atlas/tool/box/slate/normalizeCalloutBlock';
import normalizeTextBlock from '@atlas/tool/box/slate/normalizeTextBlock';

export default function withNormalElements(editor: Editor) {
    const { normalizeNode } = editor;

    editor.normalizeNode = (entry) => {
        const [node, path] = entry;

        if (!path.length) {
            // Editor normalization
            const editorNode = node as Editor;

            const lastChild = editor.children[editor.children.length - 1] as SlateBlock | undefined;

            // Merge blocks
            for (let i = editor.children.length; --i > 0; ) {
                const child = editorNode.children[i] as SlateBlock;
                const prevChild = editorNode.children[i - 1] as SlateBlock;
                if (isListBlock(child) && isListBlock(prevChild) && child.type === prevChild.type) {
                    Transforms.mergeNodes(editor, { at: [i] });
                }
            }

            if (!lastChild || !isTextBlock(lastChild)) {
                Transforms.insertNodes(editor, createTextBlock('paragraph'), {
                    at: [editor.children.length],
                });
            }
        } else if (Element.isElement(node)) {
            // Element normalization

            if (isTableBlock(node)) {
                normalizeTable(editor, node, path);
            } else if (path.length > 1) {
                const node = getNode<SlateBlock>(editor, [path[0]]);
                if (node?.type === 'table') normalizeTable(editor, node, [path[0]]);
            }

            if (isListBlock(node)) {
                const listIndex = path.slice(-1)[0];

                const nextPath = path.slice(0, -1).concat(listIndex + 1);
                const nextNode = getNode<SlateBlock>(editor, nextPath);
                if (nextNode && isListBlock(nextNode) && node.type === nextNode.type) {
                    Transforms.mergeNodes(editor, { at: nextPath });
                }

                // It is not OK to remove the node that user is interacting with, or the previous node because indexing fails then and the change is affecting the wrong node
                // if (listIndex > 0) {
                //     const prevPath = path.slice(0, -1).concat(listIndex - 1);
                //     const prevNode = getNode<SlateBlock>(editor, prevPath);
                //     if (prevNode && isListBlock(prevNode) && node.type === prevNode.type) {
                //         Transforms.mergeNodes(editor, { at: path });
                //     }
                // }
            }

            if (isCalloutBlock(node)) {
                normalizeCalloutBlock(editor, node, path);
            }

            if (isTextBlock(node)) {
                normalizeTextBlock(editor, node, path);
            }
        } else {
            // Leaf normalization
        }

        normalizeNode(entry);
    };

    return editor;
}

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('withNormalElements', async () => {
        it('adds empty text node if missing', () => {
            const editor = withNormalElements(createEditor());
            Transforms.insertNodes(editor, { type: 'paragraph', children: [] }, { at: [0] });
            expect(editor.children).toEqual([{ type: 'paragraph', children: [{ text: '' }] }]);
        });
    });
}

// #endregion
